// handles backdrop fade and menu slide/fade animations while keeping the modal mounted during exit
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, StyleProp, ViewStyle } from 'react-native';

type Return = {
  mounted: boolean;
  backdropStyle: StyleProp<ViewStyle>; // animated style for backdrop opacity
  menuAnimatedStyle: StyleProp<ViewStyle>; // animated style for menu (opacity on tablets, translateY on phones)
};

export const useModalPresentationAnimation = (
  visible: boolean,
  deviceIsTablet: boolean,
): Return => {
  const [mounted, setMounted] = useState(visible);

  // animated values
  const backdropOpacity = useRef(new Animated.Value(0)).current;
  const menuTranslateY = useRef(new Animated.Value(deviceIsTablet ? 0 : 40)).current;
  const menuOpacity = useRef(new Animated.Value(deviceIsTablet ? 0 : 1)).current;

  const animateIn = useCallback(() => {
    // reset starting values before animating in
    backdropOpacity.setValue(0);
    if (deviceIsTablet) {
      menuOpacity.setValue(0);
      menuTranslateY.setValue(0);
    } else {
      menuTranslateY.setValue(40);
      menuOpacity.setValue(1);
    }

    Animated.parallel([
      Animated.timing(backdropOpacity, {
        toValue: 1,
        duration: 200,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      deviceIsTablet
        ? Animated.timing(menuOpacity, {
            toValue: 1,
            duration: 200,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: true,
          })
        : Animated.timing(menuTranslateY, {
            toValue: 0,
            duration: 250,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: true,
          }),
    ]).start();
  }, [backdropOpacity, menuOpacity, menuTranslateY, deviceIsTablet]);

  const animateOut = useCallback(
    (onDone?: () => void) => {
      Animated.parallel([
        Animated.timing(backdropOpacity, {
          toValue: 0,
          duration: 180,
          easing: Easing.in(Easing.cubic),
          useNativeDriver: true,
        }),
        deviceIsTablet
          ? Animated.timing(menuOpacity, {
              toValue: 0,
              duration: 180,
              easing: Easing.in(Easing.cubic),
              useNativeDriver: true,
            })
          : Animated.timing(menuTranslateY, {
              toValue: 40,
              duration: 220,
              easing: Easing.in(Easing.cubic),
              useNativeDriver: true,
            }),
      ]).start(({ finished }) => {
        if (finished && onDone) {
          onDone();
        }
      });
    },
    [backdropOpacity, menuOpacity, menuTranslateY, deviceIsTablet],
  );

  // drive mounting and animations based on visibility
  useEffect(() => {
    if (visible) {
      if (!mounted) {
        setMounted(true);
      }
      requestAnimationFrame(animateIn);
    } else if (mounted) {
      animateOut(() => setMounted(false));
    }
  }, [visible, mounted, animateIn, animateOut]);

  const backdropStyle = useMemo(() => ({ opacity: backdropOpacity }), [backdropOpacity]);

  const menuAnimatedStyle = useMemo(() => {
    return deviceIsTablet
      ? { opacity: menuOpacity }
      : { transform: [{ translateY: menuTranslateY }] };
  }, [menuOpacity, menuTranslateY, deviceIsTablet]);

  return { mounted, backdropStyle, menuAnimatedStyle };
};

export default useModalPresentationAnimation;
